import { useEffect, useState } from "react";
import { ApiError, post } from "../api";
import { Loading, Refused } from "../components/Shell";
import { useApi } from "../useApi";

/**
 * The human gate, one rule at a time.
 *
 * A six-row form invites a single unread tick, which is why the rules are
 * shown one at a time. That only holds if completion means the rules were
 * actually shown. It used to mean the step index had passed them: the
 * clause chips are clickable, so clicking the last one marked every rule
 * behind it reviewed, and the page then told the signer "All 6 rules
 * reviewed" with two of six ever on screen -- a false statement about
 * their own act, in the moment before their name binds to a hash.
 *
 * So what is tracked is which clauses were opened, not how far along the
 * index is. The chips stay clickable, because going back to re-read
 * something is the behaviour of somebody doing this properly.
 */
export function Signoff() {
  const [contract, setContract] = useState("");
  const { data, error, loading, reload } = useApi<any>(
    `/signoff${contract ? `?contract=${encodeURIComponent(contract)}` : ""}`);
  const [step, setStep] = useState(1);
  //: Clauses actually put on screen. Not a count -- which ones.
  const [seen, setSeen] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [offerReplace, setOfferReplace] = useState(false);
  const [done, setDone] = useState<{ confirmed_by: string; content_hash: string } | null>(null);

  // Records the rule on screen. Runs before the early returns below, so the
  // hook order stays stable whatever the request is doing.
  useEffect(() => {
    const rows = data?.rows;
    if (!rows) return;
    const row = step <= rows.length ? rows[step - 1] : null;
    if (!row) return;
    setSeen((prev) => (prev.has(row.clause) ? prev : new Set(prev).add(row.clause)));
  }, [data, step]);

  if (loading) return <Loading />;
  if (error) return <Refused status={error.status} detail={error.detail} />;
  if (!data) return null;

  if (!data.ruleset) {
    return (
      <>
        <h1>Ruleset sign-off</h1>
        <div className="note amber">
          {data.contracts.length
            ? `No compiled ruleset is stored for ${data.contract?.reference}. Run the ingest first.`
            : "There is no contract here for you to sign off."}
        </div>
      </>
    );
  }

  const total = data.rows.length;
  const current = step <= total ? data.rows[step - 1] : null;
  const amended = data.rows.filter((r: any) => r.is_amended).length;
  const unread = data.rows.filter((r: any) => !seen.has(r.clause));
  const allRead = unread.length === 0;
  const confirmed = data.ruleset.confirmation === "confirmed";

  async function confirm(replace = false) {
    setBusy(true); setProblem(null);
    try {
      const out = await post<{ confirmed_by: string; content_hash: string }>(
        "/signoff/confirm",
        { contract_id: data.contract.id, sheet_hash: data.ruleset.content_hash, replace });
      setDone(out);
      reload();
    } catch (e) {
      const detail = e instanceof ApiError ? e.detail : String(e);
      // Already confirmed at this hash. The server refuses unless told to
      // replace, so the choice is put to the person in those words rather
      // than made for them.
      setProblem(detail);
      setOfferReplace(e instanceof ApiError && e.status === 409 && /replace: true/.test(detail));
    } finally { setBusy(false); }
  }

  if (done) {
    return (
      <>
        <h1>Ruleset confirmed</h1>
        <p className="standfirst">The clock engine will now run against these rules.</p>
        <div className="note">
          <span className="h">Recorded</span>
          Confirmed by <strong>{done.confirmed_by}</strong> against content hash{" "}
          <code>{done.content_hash}</code>. Written to the audit journal.
        </div>
      </>
    );
  }

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 24, alignItems: "flex-start" }}>
        <div>
          <h1>{confirmed ? "The rules Neev is running on" : "Confirm the rules Neev will run on"}</h1>
          <p className="standfirst">
            Compiled from this contract's own words, one rule at a time. A
            signature covers all {total}, so all {total} are shown.
          </p>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div className="mn">{data.contract?.reference} · v{data.ruleset.version}</div>
          <div className="mn withheld" style={{ fontSize: 11 }}>{data.ruleset.content_hash}</div>
        </div>
      </div>

      {/* Whether the gate has been passed, and by whom. The API has always
          known; the screen read as a pending action either way, which left
          an auditor without the one fact they come here for. */}
      <div className={`note${confirmed ? "" : " amber"}`}>
        <span className="h">
          {confirmed ? "Confirmed" : "Not yet confirmed"}
        </span>
        {confirmed ? (
          <>
            <span aria-label={`These rules were confirmed by ${data.ruleset.confirmed_by} against hash ${data.ruleset.content_hash}, which is the hash compiled today.`}>
              These rules were confirmed by <strong>{data.ruleset.confirmed_by}</strong>{" "}
              against hash <code>{data.ruleset.content_hash}</code>, which is the hash
              compiled today
            </span>
            {" "}— so the signature covers what is running. The clock
            engine is evaluating against them now. Confirming again keeps that
            record in the chain and puts yours on top.
            {(data.ruleset.previously ?? []).length > 0 && (
              <div className="hint" style={{ marginTop: 8 }}>
                Earlier confirmations at this hash:{" "}
                {data.ruleset.previously.map((p: any) => `${p.by} (${(p.at ?? "").slice(0, 10)})`).join("; ")}.
              </div>
            )}
          </>
        ) : (
          <>
            Nothing is evaluated until these rules are confirmed. The clock
            engine refuses to run against an unconfirmed ruleset.
          </>
        )}
      </div>

      {data.contracts.length > 1 && (
        <nav className="chips">
          {data.contracts.map((c: any) => (
            <button key={c.id} type="button"
              className={`chip${c.id === data.contract?.id ? " on" : ""}`}
              onClick={() => { setContract(c.reference); setStep(1); setSeen(new Set()); }}>
              {c.reference}
            </button>
          ))}
        </nav>
      )}

      <ol style={{ display: "flex", gap: 6, listStyle: "none", padding: 0, margin: "22px 0 0" }}>
        {data.rows.map((r: any, i: number) => (
          <li key={r.clause} style={{ flex: 1 }}>
            <button type="button" onClick={() => setStep(i + 1)}
              style={{ display: "block", width: "100%", border: 0, background: "none",
                       padding: 0, cursor: "pointer", textAlign: "left" }}>
              {/* Filled because it was read, not because the index went past it. */}
              <span style={{ display: "block", height: 5, borderRadius: 3,
                background: i + 1 === step ? "var(--accent)"
                  : seen.has(r.clause) ? "var(--accent)" : "var(--rule)" }} />
              <span className="mn" style={{ display: "block", fontSize: 11.5, marginTop: 7,
                color: i + 1 === step ? "var(--accent)" : "var(--muted)",
                fontWeight: i + 1 === step ? 600 : 400 }}>
                {r.clause}{r.is_amended && " ●"}
              </span>
            </button>
          </li>
        ))}
      </ol>
      <p className="hint">
        {step <= total ? `Rule ${step} of ${total}` : "Summary"} · {seen.size} of{" "}
        {total} read · {amended} depart from unamended NEC4
      </p>

      {current ? (
        <article className="card">
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <span className="ser" style={{ fontSize: 24, fontWeight: 600 }}>Clause {current.clause}</span>
            {/* A departure from unamended NEC4 is exactly what colour is
                for: it is the deviation a reviewer is here to catch. */}
            <span className={`pill${current.is_amended ? " act" : ""}`}>
              {current.is_amended ? "amended by this contract" : "unamended NEC4"}
            </span>
            <span style={{ marginLeft: "auto", fontSize: 13 }}>
              Who must act <strong>{current.responsible}</strong>
            </span>
          </div>

          <p className="ser" style={{ fontSize: 17, lineHeight: 1.62 }}>{current.plain_english}</p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13, margin: "18px 0" }}>
            <div className="card" style={{ margin: 0, background: current.is_amended ? "var(--amber-soft)" : "var(--sunken)" }}>
              <div className="k">Period Neev will apply</div>
              <div className="mn" style={{ fontSize: 23, fontWeight: 600 }}>{current.applied_period}</div>
            </div>
            <div className="card" style={{ margin: 0, background: "var(--sunken)" }}>
              <div className="k">Unamended NEC4</div>
              <div className="mn withheld" style={{ fontSize: 23, fontWeight: 600,
                textDecoration: current.is_amended ? "line-through" : undefined }}>
                {current.default_period}
              </div>
            </div>
          </div>

          <div className="note red"><span className="h">If nobody replies</span>{current.consequence}</div>

          <details open={current.is_amended}>
            <summary className="k" style={{ cursor: "pointer" }}>
              The words this rests on — <span style={{ textTransform: "none", letterSpacing: 0 }}>{current.source_citation}</span>
            </summary>
            <p className="ser" style={{ fontStyle: "italic", background: "var(--sunken)",
              padding: "15px 17px", borderRadius: 9, lineHeight: 1.72 }}>
              “{current.source_text}”
            </p>
          </details>

          {current.review_flags.map((f: string, i: number) => (
            <div className="note red" key={i}>{f}</div>
          ))}

          <nav style={{ display: "flex", justifyContent: "space-between", marginTop: 18 }}>
            {step > 1 ? <button className="btn" onClick={() => setStep(step - 1)}>Back</button> : <span />}
            <button className="primary" onClick={() => setStep(step + 1)}>
              {step === total ? "Review complete" : "Next rule"} →
            </button>
          </nav>
        </article>
      ) : (
        <article className="card">
          <h2 className="ser" style={{ fontSize: 24, fontWeight: 500, margin: 0 }}>
            {allRead ? `All ${total} rules read` : `${seen.size} of ${total} rules read`}
          </h2>
          {/* A gate that reports completion it cannot see manufactures a
              false record of diligence, which is worse than no gate. */}
          {!allRead && (
            <div className="note amber">
              <span className="h">
                {unread.length} {unread.length === 1 ? "rule has" : "rules have"} not been shown to you
              </span>
              {unread.map((r: any) => `Clause ${r.clause}`).join(", ")}. Your
              signature would cover {total} rules, including {unread.length} you
              have not read. Open {unread.length === 1 ? "it" : "them"} first.
            </div>
          )}
          {data.may_confirm ? (
            <>
              <p>
                Your signature binds to hash <code>{data.ruleset.content_hash}</code>. If
                the contract is recompiled after you sign, the hash moves and the
                confirmation is refused — your signature would no longer cover what
                goes live.
              </p>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 18,
                            gap: 12, flexWrap: "wrap" }}>
                <button className="btn" onClick={() => setStep(total)}>Back</button>
                {allRead ? (
                  <button className="primary" disabled={busy} onClick={() => confirm(false)}>
                    Confirm all {total} rules
                  </button>
                ) : (
                  <button className="primary"
                    onClick={() => setStep(data.rows.indexOf(unread[0]) + 1)}>
                    Read clause {unread[0].clause} →
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="note amber">
              <span className="h">You may read this, but not sign it</span>
              Confirming a ruleset requires the contract authority role. Compiled
              rules start unconfirmed and the clock engine refuses to run against
              them — if any signed-in user could confirm them, the gate would be
              theatre.
            </div>
          )}
          {problem && <div className="err">{problem}</div>}
          {offerReplace && (
            <button className="btn" disabled={busy} style={{ marginTop: 8 }}
              onClick={() => { setOfferReplace(false); confirm(true); }}>
              Yes — put my confirmation on top and keep the earlier one in the chain
            </button>
          )}
        </article>
      )}
    </>
  );
}
